/**
 * WebAudio sink for the APU sample queue. Browser-only glue — lives outside
 * src/hardware so the APU stays headless-testable. index.ts (integrator)
 * wires this up.
 *
 * Design notes (bug history):
 * - AudioContext creation/resume must happen inside (or be retried after) a
 *   user gesture; autoplay policies silently suspend contexts otherwise.
 * - ScriptProcessorNode with 0 *input* channels never fires onaudioprocess
 *   in Safari -> total silence. We request 1 input channel and ignore it.
 * - The queue is the clock boundary between the emulator (rAF-driven) and the
 *   audio hardware clock. If the emulator outruns audio (e.g. 120 Hz rAF on
 *   ProMotion displays), we drop the oldest backlog to bound latency; on
 *   underrun we hold the last sample instead of emitting crackly zeros.
 */

export interface SampleSource {
    drainSamples(): Float32Array;
}

export class ApuAudioPlayer {
    private ctx: AudioContext | null = null;
    private node: ScriptProcessorNode | null = null;
    private gain: GainNode | null = null;
    private source: SampleSource;
    private readonly sampleRate: number;
    private readonly volume: number;
    private lastSample = 0;
    /** Max backlog we will play, in samples (~93 ms at 44.1 kHz). */
    private static readonly MAX_BACKLOG = 4096;
    private static readonly BUFFER_SIZE = 2048;

    constructor(source: SampleSource, sampleRate: number = 44100, volume: number = 0.25) {
        this.source = source;
        this.sampleRate = sampleRate;
        this.volume = volume;
    }

    /** Follow a new APU instance (e.g. after a ROM reload). */
    setSource(source: SampleSource): void {
        this.source = source;
        this.lastSample = 0;
    }

    /**
     * Start (or resume) playback. Safe to call repeatedly and from anywhere;
     * if the AudioContext cannot run yet (autoplay policy), a one-time
     * gesture listener retries resume automatically.
     */
    start(): void {
        if (typeof window === 'undefined') return; // headless: no-op
        if (!this.ctx) {
            const Ctor: typeof AudioContext | undefined =
                window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
            if (!Ctor) return;
            try {
                this.ctx = new Ctor({ sampleRate: this.sampleRate });
            } catch {
                this.ctx = new Ctor(); // fall back to device rate; slight pitch drift beats silence
            }
            this.gain = this.ctx.createGain();
            this.gain.gain.value = this.volume;
            this.gain.connect(this.ctx.destination);

            // 1 input channel on purpose: Safari never fires onaudioprocess
            // for 0-input ScriptProcessorNodes.
            this.node = this.ctx.createScriptProcessor(ApuAudioPlayer.BUFFER_SIZE, 1, 1);
            this.node.onaudioprocess = (event: AudioProcessingEvent) => {
                const out = event.outputBuffer.getChannelData(0);
                let samples = this.source.drainSamples();
                // Overrun: emulator outpaced the audio clock — drop the oldest
                // backlog so latency stays bounded and pitch stays correct.
                if (samples.length > ApuAudioPlayer.MAX_BACKLOG) {
                    samples = samples.subarray(samples.length - ApuAudioPlayer.MAX_BACKLOG);
                }
                const n = Math.min(out.length, samples.length);
                out.set(samples.subarray(0, n));
                // Underrun: hold the last sample instead of crackly zeros.
                if (n > 0) this.lastSample = samples[n - 1];
                if (n < out.length) out.fill(this.lastSample, n);
            };
            this.node.connect(this.gain);
        }

        if (this.ctx.state !== 'running') {
            void this.ctx.resume().catch(() => { /* retried on next gesture */ });
            // Autoplay policy fallback: resume on the next user gesture anywhere.
            const retry = () => {
                if (this.ctx && this.ctx.state !== 'running') {
                    void this.ctx.resume().catch(() => { /* give up quietly */ });
                }
            };
            window.addEventListener('click', retry, { once: true });
            window.addEventListener('keydown', retry, { once: true });
        }
    }

    /** Suspend audio (e.g. when emulation is stopped). */
    stop(): void {
        if (this.ctx && this.ctx.state === 'running') {
            void this.ctx.suspend().catch(() => { /* noop */ });
        }
    }
}
